/**
 * Token Manager — Handles token expiry, auto-refresh, and 401 error recovery
 *
 * Features:
 * - Stores token + expiry time in localStorage
 * - Auto-refreshes token before expiry (preemptive, not on-demand)
 * - Handles 401 responses by attempting refresh with cross-tab coordination
 * - Guarded logout: only clears tokens if not rotated by another tab
 * - Hard-redirects to /login when session is genuinely ended (auth refresh failure)
 * - Recovers or logs out on tab focus after long idle
 */

// eslint-disable-next-line import/no-cycle
import { acquireRefreshLock } from './tokenRefreshLock';
import logger from './logger';

const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';
const REFRESH_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/refresh-token`;

// Singleton state
let refreshPromise = null; // Prevent concurrent refresh calls
let lastFailedRefreshToken = null; // Track which token failed to prevent infinite retry loops
let isInitialized = false; // Prevent duplicate listeners
let isRedirectingToLogin = false;

const AUTH_PATH_PREFIXES = ['/login'];

const isOnAuthPage = () => {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname || '';
  return AUTH_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
};

/**
 * After a scheduled/visibility refresh: redirect only on genuine auth failure
 * (tokens cleared). Transient errors keep the existing "retry later" behavior.
 */
const redirectIfRefreshAuthFailed = (result) => {
  if (!result || result.success || !result.isAuthError) return;
  if (getToken()) return; // Another tab may have rotated a valid token
  redirectToLogin();
};

/**
 * Store tokens + expiry time
 */
export const setTokens = (tokenData) => {
  if (!tokenData || !tokenData.token) {
    clearTokens();
    return;
  }

  try {
    localStorage.setItem(TOKEN_KEY, tokenData.token);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokenData.refresh_token);

    // Clear the failed token flag since we have new valid tokens
    lastFailedRefreshToken = null;

    // Calculate expiry time: now + expires_in (seconds) - 10s buffer (refresh 10s before actual expiry)
    const expiryTime = Date.now() + (tokenData.expires_in - 10) * 1000;
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

    // Schedule auto-refresh
    scheduleTokenRefresh();
  } catch (err) {
    logger.error('Error storing tokens:', err);
  }
};

/**
 * Retrieve stored token
 */
export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (err) {
    logger.error('Error retrieving token:', err);
    return null;
  }
};

/**
 * Retrieve stored refresh token (internal, for compare-and-swap)
 */
const getRefreshToken = () => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (err) {
    logger.error('Error retrieving refresh token:', err);
    return null;
  }
};

/**
 * Check if token is expired (or expiring soon)
 */
export const isTokenExpired = () => {
  try {
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryTime) return true;

    const now = Date.now();
    return now >= parseInt(expiryTime, 10);
  } catch (err) {
    logger.error('Error checking token expiry:', err);
    return true;
  }
};

/**
 * Get time remaining until token expiry (in ms)
 */
export const getTokenTimeRemaining = () => {
  try {
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryTime) return 0;

    const remaining = parseInt(expiryTime, 10) - Date.now();
    return Math.max(0, remaining);
  } catch (err) {
    logger.error('Error getting token time remaining:', err);
    return 0;
  }
};

const MAX_RETRIES = 3;
const RETRY_DELAY_BASE = 1000; // 1s base delay for exponential backoff

/**
 * Internal: Perform the actual token refresh (called inside lock)
 */
const performRefresh = async (retryCount = 0, failedRefreshToken = null) => {
  try {
    const refreshTokenValue = getRefreshToken();
    if (!refreshTokenValue) {
      logger.warn('[TokenManager] No refresh token available');
      clearTokensGuarded(failedRefreshToken);
      return { success: false, error: 'No refresh token', isAuthError: true };
    }

    logger.log(`[TokenManager] Attempting refresh at ${new Date().toLocaleTimeString()} from:`, REFRESH_ENDPOINT, `(attempt ${retryCount + 1}/${MAX_RETRIES + 1})`);

    const response = await fetch(REFRESH_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: refreshTokenValue,
      }),
    });

    logger.log(`[TokenManager] Refresh response status at ${new Date().toLocaleTimeString()}:`, response.status);

    // Handle auth errors (401/403) - GUARDED clear: only if token wasn't rotated by another tab
    if (response.status === 401 || response.status === 403) {
      logger.warn('[TokenManager] Refresh failed with auth error', response.status);
      // Track this failed token to prevent other tabs from retrying it
      lastFailedRefreshToken = refreshTokenValue;
      clearTokensGuarded(failedRefreshToken || refreshTokenValue);
      return { success: false, error: `HTTP ${response.status}`, isAuthError: true };
    }

    // Handle server errors (5xx) - transient, retry with backoff
    if (response.status >= 500) {
      if (retryCount < MAX_RETRIES) {
        const delayMs = RETRY_DELAY_BASE * Math.pow(2, retryCount);
        logger.warn(`[TokenManager] Server error ${response.status}, retrying in ${delayMs}ms...`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
        return performRefresh(retryCount + 1, failedRefreshToken || refreshTokenValue);
      } else {
        logger.error('[TokenManager] Max retries reached for server error (5xx)');
        return { success: false, error: `HTTP ${response.status} after ${MAX_RETRIES} retries`, isAuthError: false };
      }
    }

    // Other non-2xx errors
    if (!response.ok) {
      logger.warn(`[TokenManager] Refresh token failed with status ${response.status}`);
      const errorBody = await response.text();
      logger.warn('[TokenManager] Error body:', errorBody);
      return { success: false, error: `HTTP ${response.status}`, isAuthError: false };
    }

    const data = await response.json();
    logger.log('[TokenManager] Refresh response data:', data);

    // Extract token data from response (adjust path if API structure differs)
    const newTokenData = data?.data?.data?.token || data?.data?.token || data?.token;
    if (!newTokenData || !newTokenData.token) {
      logger.warn('[TokenManager] Invalid token response structure. Full response:', data);
      return { success: false, error: 'Invalid response', isAuthError: false };
    }

    logger.log(`[TokenManager] Token refreshed successfully at ${new Date().toLocaleTimeString()}`);
    setTokens(newTokenData);
    return { success: true, newTokenData };
  } catch (err) {
    // Network errors - retry with backoff
    if (retryCount < MAX_RETRIES) {
      const delayMs = RETRY_DELAY_BASE * Math.pow(2, retryCount);
      logger.warn(`[TokenManager] Network error (attempt ${retryCount + 1}/${MAX_RETRIES + 1}), retrying in ${delayMs}ms...`, err.message);
      await new Promise(resolve => setTimeout(resolve, delayMs));
      return performRefresh(retryCount + 1, failedRefreshToken);
    } else {
      logger.error('[TokenManager] Max retries reached for network error:', err.message);
      return { success: false, error: err.message, isAuthError: false };
    }
  }
};

/**
 * Refresh token via backend API with cross-tab coordination
 * Uses navigator.locks to ensure only one tab refreshes at a time
 * Returns { success, newTokenData, error, isAuthError } for complex handling
 */
export const refreshToken = async (retryCount = 0) => {
  const result = await acquireRefreshLock(async () => {
    const currentRefreshToken = getRefreshToken();

    // LOOP PREVENTION: If this token just failed in another tab, don't retry it
    if (lastFailedRefreshToken && currentRefreshToken === lastFailedRefreshToken) {
      logger.warn('[TokenManager] This token just failed in another tab, skipping retry to prevent infinite loop');
      clearTokensGuarded(currentRefreshToken);
      return { success: false, error: 'Token already failed', isAuthError: true };
    }

    return performRefresh(retryCount, currentRefreshToken);
  });

  if (result === null) {
    logger.warn('[TokenManager] Could not acquire refresh lock, another tab may be refreshing');
    return { success: false, error: 'Refresh in progress', isAuthError: false };
  }

  return result;
};

/**
 * Simpler refresh for interceptor use: returns just the access token or throws
 * Used by axios interceptor for straightforward 401 recovery
 * Delegates to handle401Response() to ensure single-flight across all callers
 */
export const refreshAccessToken = async () => {
  const { shouldRetry, newToken } = await handle401Response();
  if (shouldRetry && newToken) {
    return newToken;
  }
  throw new Error('Refresh failed');
};

/**
 * GUARDED clear: Only clears tokens if stored refresh token still equals the failed one.
 * Prevents clearing valid tokens rotated by another tab (compare-and-swap pattern).
 */
const clearTokensGuarded = (failedRefreshToken) => {
  try {
    const currentStoredToken = getRefreshToken();

    // Only clear if no other tab rotated the token
    if (failedRefreshToken && currentStoredToken !== failedRefreshToken) {
      logger.warn(
        '[TokenManager] Refresh token was rotated by another tab. Not clearing tokens.'
      );
      return; // Another tab has valid token
    }

    // Token wasn't rotated or explicit logout (failedToken=null) — safe to clear
    logger.log('[TokenManager] Clearing tokens (genuinely invalid)');
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);

    if (refreshTimeoutId) {
      clearTimeout(refreshTimeoutId);
      refreshTimeoutId = null;
    }

    refreshPromise = null;
  } catch (err) {
    logger.error('Error clearing tokens:', err);
  }
};

/**
 * Handle 401 response — attempt refresh and retry
 * Returns: { shouldRetry: true, newToken: ... } or { shouldRetry: false }
 *
 * Behavior:
 * - Auth errors (401/403): Guarded clear + redirect (only if genuinely logged out)
 * - Network/server errors: Don't clear tokens, let request fail (user can retry)
 */
export const handle401Response = async () => {
  // Prevent concurrent refresh attempts within this tab
  if (refreshPromise) {
    const result = await refreshPromise;
    return result;
  }

  refreshPromise = (async () => {
    try {
      const result = await refreshToken();
      if (result.success) {
        logger.log('[TokenManager] 401 recovery successful, retrying request');
        return { shouldRetry: true, newToken: result.newTokenData.token };
      } else if (result.isAuthError) {
        // Auth error - check if valid token exists from another tab
        logger.warn('[TokenManager] Auth error during refresh');

        const currentToken = getToken();
        if (!currentToken) {
          logger.warn('[TokenManager] No valid token, redirecting to login');
          redirectToLogin();
        } else {
          logger.log('[TokenManager] Valid token exists (rotated by another tab), retrying');
          return { shouldRetry: true, newToken: currentToken };
        }

        return { shouldRetry: false };
      } else {
        // Transient error - preserve tokens
        logger.warn('[TokenManager] Transient error during refresh, failing request (tokens preserved)');
        return { shouldRetry: false };
      }
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Schedule auto-refresh of token before expiry
 */
let refreshTimeoutId = null;

export const scheduleTokenRefresh = () => {
  // Clear existing timeout
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
  }

  const timeRemaining = getTokenTimeRemaining();
  if (timeRemaining <= 0) {
    // Token already expired, refresh immediately
    refreshToken()
      .then(redirectIfRefreshAuthFailed)
      .catch(err => logger.error('Immediate refresh failed:', err));
    return;
  }

  // Schedule refresh with a small jitter to avoid thundering herd
  const delayMs = timeRemaining + Math.random() * 1000;

  refreshTimeoutId = setTimeout(() => {
    logger.log(`[TokenManager] Auto-refreshing token at ${new Date().toLocaleTimeString()}...`);
    refreshToken()
      .then(redirectIfRefreshAuthFailed)
      .catch(err => logger.error('[TokenManager] Auto-refresh failed:', err));
  }, delayMs);
};

/**
 * Clear all stored tokens (explicit logout)
 */
export const clearTokens = () => {
  // On explicit logout, clear with no failedToken (forces clear)
  clearTokensGuarded(null);
};

/**
 * Listen for token updates from other tabs via storage events
 */
const setupStorageListener = () => {
  if (typeof window === 'undefined') return;

  const handleStorageChange = (event) => {
    // Detect when another tab logs out (clears access token)
    if (event.key === TOKEN_KEY && event.newValue === null) {
      logger.log('[TokenManager] Access token cleared by another tab (logout), following logout');
      redirectToLogin();
      return;
    }

    // Detect when another tab rotates the refresh token
    if (event.key === REFRESH_TOKEN_KEY && event.newValue && event.newValue !== event.oldValue) {
      logger.log('[TokenManager] Token rotated by another tab, rescheduling auto-refresh');
      scheduleTokenRefresh();
    }

    // Detect when another tab updates token expiry
    if (event.key === TOKEN_EXPIRY_KEY && event.newValue) {
      logger.log('[TokenManager] Token expiry updated by another tab, rescheduling');
      scheduleTokenRefresh();
    }
  };

  try {
    window.addEventListener('storage', handleStorageChange);
    logger.log('[TokenManager] Cross-tab storage listener initialized');
  } catch (err) {
    logger.warn('[TokenManager] Failed to setup storage listener:', err.message);
  }
};

/**
 * Redirect to login page when the session is genuinely ended.
 * Clears tokens, notifies listeners, and hard-navigates so the user
 * is not left on a protected page seeing repeated 401s.
 */
const redirectToLogin = () => {
  try {
    if (isRedirectingToLogin) return;
    if (isOnAuthPage()) {
      clearTokens();
      return;
    }

    isRedirectingToLogin = true;
    logger.warn('Session expired, clearing tokens. Redirect to login.');
    clearTokens();

    // Dispatch custom event that components can listen to
    window.dispatchEvent(new CustomEvent('token-expired'));

    window.location.replace('/login');
  } catch (err) {
    isRedirectingToLogin = false;
    logger.error('Error redirecting to login:', err);
  }
};

/**
 * When user returns to a long-idle tab: recover session or send to login.
 * Does not change preemptive refresh / 401 retry behavior.
 */
const setupVisibilityListener = () => {
  if (typeof document === 'undefined') return;

  const handleVisibilityChange = async () => {
    if (document.hidden || isOnAuthPage() || isRedirectingToLogin) return;

    const token = getToken();
    if (!token) {
      // Tokens already cleared (e.g. failed background refresh) — leave protected UI
      logger.warn('[TokenManager] No token on tab focus, redirecting to login');
      redirectToLogin();
      return;
    }

    if (!isTokenExpired()) return;

    logger.log('[TokenManager] Token expired on tab focus, attempting refresh...');
    try {
      const result = await refreshToken();
      redirectIfRefreshAuthFailed(result);
    } catch (err) {
      logger.error('[TokenManager] Unexpected error during visibility refresh:', err);
    }
  };

  try {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    logger.log('[TokenManager] Visibility listener initialized');
  } catch (err) {
    logger.warn('[TokenManager] Failed to setup visibility listener:', err.message);
  }
};

/**
 * Initialize token manager on app load
 * Call this once in your app's entry point or after login
 */
export const initializeTokenManager = () => {
  if (!isInitialized) {
    setupStorageListener();
    setupVisibilityListener();
    isInitialized = true;
  }

  const token = getToken();
  if (token && !isTokenExpired()) {
    scheduleTokenRefresh();
  } else if (token && isTokenExpired()) {
    logger.log('[TokenManager] Token expired on init, attempting refresh...');
    refreshToken().then(result => {
      redirectIfRefreshAuthFailed(result);
      // For transient errors, don't redirect - let user try again on next request
    }).catch(err => {
      logger.error('[TokenManager] Unexpected error during init refresh:', err);
      // Don't redirect on unexpected errors - let user retry
    });
  }
};
